/**
 * Ordenação por clique no cabeçalho — em TODA tabela do sistema, como no
 * Windows Explorer (pedido do Lucas, 10/08): um clique ordena do menor ao
 * maior; outro clique inverte. A seta no cabeçalho diz o estado.
 *
 * É UM ouvinte no documento, como o das fotos: cobre toda tabela `.tabela`
 * que existe e toda que ainda vai existir, sem nenhuma tela precisar aderir.
 * A ordenação move as PRÓPRIAS linhas (os ouvintes delas sobrevivem — nada é
 * recriado), e entende o conteúdo:
 *
 *   - número no formato brasileiro ("1.234,56", "R$ 850,50", "45 un");
 *   - data/hora brasileira ("16/07/2026", "16/07/2026 14:30");
 *   - o resto compara como texto pt-BR com ordem natural ("HID-9" < "HID-10").
 *
 * Célula vazia (ou "—") vai para o FIM nas duas direções — vazio não é valor.
 *
 * Tabela paginada no servidor (Estoque) marca `data-ordenacao-propria` e cuida
 * do próprio clique: ordenar só a página visível seria mentir a ordenação.
 */

const VAZIO = Symbol('vazio');

type Valor = number | string | typeof VAZIO;

function valorDe(texto: string): Valor {
  const limpo = texto.trim();
  if (!limpo || limpo === '—' || limpo === '-') return VAZIO;

  // Data (com hora opcional): vira número para comparar cronologicamente.
  const data = limpo.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ ,]+(\d{2}):(\d{2}))?/);
  if (data) {
    return Number(
      `${data[3]}${data[2]}${data[1]}${data[4] ?? '00'}${data[5] ?? '00'}`,
    );
  }

  // Número brasileiro, possivelmente com R$/unidade em volta.
  const num = limpo.replace(/^R\$\s*/, '').match(/^-?[\d.,]+/);
  if (num) {
    let s = num[0];
    if (s.includes('.') && s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    else if (s.includes(',')) s = s.replace(',', '.');
    else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
    const n = Number(s);
    // Só é número se o que sobrou depois dele for sufixo curto ("un", "km/l").
    if (Number.isFinite(n) && limpo.replace(/^R\$\s*/, '').slice(num[0].length).trim().length <= 6) {
      return n;
    }
  }

  return limpo.toLowerCase();
}

function comparar(a: Valor, b: Valor, crescente: boolean): number {
  // Vazio SEMPRE por último, independente da direção.
  if (a === VAZIO && b === VAZIO) return 0;
  if (a === VAZIO) return 1;
  if (b === VAZIO) return -1;
  let r: number;
  if (typeof a === 'number' && typeof b === 'number') r = a - b;
  else {
    r = String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
  }
  return crescente ? r : -r;
}

export function instalarOrdenacaoDeTabelas(): void {
  document.addEventListener('click', (e) => {
    const th = (e.target as HTMLElement).closest<HTMLTableCellElement>('th');
    if (!th || !th.textContent?.trim()) return;
    const tabela = th.closest('table');
    if (!tabela?.classList.contains('tabela')) return;
    if (tabela.hasAttribute('data-ordenacao-propria')) return;
    const corpo = tabela.tBodies[0];
    if (!corpo || corpo.rows.length < 2) return;

    const coluna = [...th.parentElement!.children].indexOf(th);
    const crescente = th.getAttribute('aria-sort') !== 'ascending';
    tabela.querySelectorAll('th[aria-sort]').forEach((x) => x.removeAttribute('aria-sort'));
    th.setAttribute('aria-sort', crescente ? 'ascending' : 'descending');

    // O valor é calculado UMA vez por linha antes de ordenar — recalcular a
    // cada comparação faria a tabela grande rastejar.
    const linhas: { linha: HTMLTableRowElement; valor: Valor }[] = [...corpo.rows].map(
      (linha) => ({
        linha,
        valor: valorDe(linha.cells[coluna]?.textContent ?? ''),
      }),
    );
    linhas.sort((x, y) => comparar(x.valor, y.valor, crescente));
    for (const { linha } of linhas) corpo.appendChild(linha);
  });
}
